import type {
    NavigationItemData,
    SponsorData,
} from '@beat-venues/common-types';
import { useTranslation } from 'next-i18next';
import { ReactNode, type JSX } from 'react';
import { type VariantProps, tv } from 'tailwind-variants';

import { ButtonOrLink, CloseIcon, Text, TextLink } from '../../atoms';
import { SponsorLink } from '../../molecules';
import type { CommonProps } from '../../types';

export interface MobileMenuProps
    extends CommonProps,
        VariantProps<typeof MobileMenuStyles> {
    items: NavigationItemData[];
    secondaryItems?: NavigationItemData[];
    sponsor?: SponsorData | null;
    open?: boolean;
    children: ReactNode;
    onClose?: () => void;
}

export const MobileMenuStyles = tv({
    slots: {
        root: 'px-6 pb-14 bg-black h-screen w-100 fixed inset-0 overflow-auto z-50',
    },
    variants: {
        open: {
            true: {
                root: 'block',
            },
            false: {
                root: 'hidden',
            },
        },
    },
    defaultVariants: {
        open: false,
    },
});

const renderMobileMenuItem = (
    item: NavigationItemData,
    onClickCallback?: () => void,
    IsSubMenuItem = false
) => (
    <TextLink
        className={
            IsSubMenuItem
                ? `text-3xl leading-10 hover:no-underline`
                : `text-4xl leading-[3rem] hover:no-underline`
        }
        href={item.href}
        underline={false}
        onClick={onClickCallback}
    >
        {item.label}
    </TextLink>
);
export const MobileMenu = ({
    items,
    secondaryItems,
    open,
    sponsor,
    onClose,
    className,
    testId = 'mobile-menu',
    children,
    ...props
}: MobileMenuProps): JSX.Element => {
    const { root } = MobileMenuStyles();
    const { t } = useTranslation();

    return (
        <nav
            aria-label="Main"
            className={root({ open, className })}
            data-testid={testId}
            role="navigation"
            {...props}
        >
            <div className="flex justify-between bg-inherit pb-7 pt-8">
                <div className="flex gap-6">{children}</div>

                <ButtonOrLink
                    aria-expanded={open}
                    className="focus"
                    onClick={onClose}
                >
                    <Text className="sr-only">
                        {t('closeMenu', {
                            ns: 'global',
                        })}
                    </Text>
                    <CloseIcon size="lg" />
                </ButtonOrLink>
            </div>
            <ul>
                {items.map((item) => (
                    <li key={item.id} className="mb-0.5">
                        {item.href ? (
                            renderMobileMenuItem(item, onClose)
                        ) : (
                            <>
                                <Text className="text-4xl leading-[3rem]">
                                    {item.label}
                                </Text>
                                {item.items && (
                                    <ul className="pt-4">
                                        {item.items?.length &&
                                            item.items.map((subItem) => (
                                                <li
                                                    key={subItem.id}
                                                    className="mb-1"
                                                >
                                                    {renderMobileMenuItem(
                                                        subItem,
                                                        onClose,
                                                        true
                                                    )}
                                                </li>
                                            ))}
                                    </ul>
                                )}
                            </>
                        )}
                    </li>
                ))}
            </ul>
            {secondaryItems && secondaryItems.length !== 0 && (
                <ul>
                    {secondaryItems.map((item) => (
                        <li key={item.id} className="mb-1">
                            {renderMobileMenuItem(item, onClose, true)}
                        </li>
                    ))}
                </ul>
            )}
            {sponsor && <SponsorLink className="mt-10 w-4/6" {...sponsor} />}
        </nav>
    );
};
